import type { Location } from 'react-router-dom';

const AUTH_OVERLAY_BACKGROUND_STORAGE_KEY = 'auth_overlay:last_background_location';

export type AuthOverlayRouteState = {
  authOverlayBackgroundLocation?: Location;
};

const AUTH_OVERLAY_BLOCKED_BACKGROUND_PREFIXES = [
  '/account-settings',
  '/add-bot',
  '/ai-agent',
  '/authorize-action',
  '/backtest',
  '/checkout',
  '/choose-plan',
  '/deploy',
  '/feedback',
  '/login',
  '/my-',
  '/onboarding',
  '/pro-success',
  '/set-password',
  '/signup-survey',
  '/stripe/success',
  '/video',
];

const toStoredLocation = (
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
): Location => ({
  pathname: location.pathname,
  search: location.search || '',
  hash: location.hash || '',
  state: null,
  key: 'auth-overlay-stored',
});

export const getAuthOverlayBackgroundLocation = (state: unknown): Location | null => {
  if (!state || typeof state !== 'object') {
    return null;
  }

  const maybeLocation = (state as AuthOverlayRouteState).authOverlayBackgroundLocation;
  if (!maybeLocation || typeof maybeLocation !== 'object') {
    return null;
  }

  if (typeof maybeLocation.pathname !== 'string') {
    return null;
  }

  return maybeLocation;
};

export const getDefaultAuthOverlayBackgroundLocation = (): Location => ({
  pathname: '/',
  search: '',
  hash: '',
  state: null,
  key: 'auth-overlay-default',
});

export const buildAuthOverlayRouteState = (
  backgroundLocation?: Location | null,
): AuthOverlayRouteState | undefined => {
  if (!backgroundLocation || typeof backgroundLocation.pathname !== 'string') {
    return undefined;
  }

  return {
    authOverlayBackgroundLocation: backgroundLocation,
  };
};

export const canUseStoredAuthOverlayBackgroundLocation = (
  location?: Pick<Location, 'pathname'> | null,
): boolean => {
  if (!location || typeof location.pathname !== 'string') {
    return false;
  }

  return !AUTH_OVERLAY_BLOCKED_BACKGROUND_PREFIXES.some((prefix) => (
    prefix.endsWith('-')
      ? location.pathname.startsWith(prefix)
      : location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  ));
};

export const rememberAuthOverlayBackgroundLocation = (
  location?: Pick<Location, 'pathname' | 'search' | 'hash'> | null,
): void => {
  if (
    typeof window === 'undefined' ||
    !location ||
    !canUseStoredAuthOverlayBackgroundLocation(location)
  ) {
    return;
  }

  try {
    window.sessionStorage?.setItem(
      AUTH_OVERLAY_BACKGROUND_STORAGE_KEY,
      JSON.stringify(toStoredLocation(location)),
    );
  } catch {
    // Ignore storage failures; explicit route state still carries backdrop.
  }
};

export const getStoredAuthOverlayBackgroundLocation = (): Location | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedValue = window.sessionStorage?.getItem(AUTH_OVERLAY_BACKGROUND_STORAGE_KEY);
    if (!storedValue) {
      return null;
    }

    const parsed = JSON.parse(storedValue) as Partial<Location> | null;
    if (
      !parsed ||
      typeof parsed.pathname !== 'string' ||
      parsed.pathname === '/sales' ||
      parsed.pathname.startsWith('/sales/') ||
      !canUseStoredAuthOverlayBackgroundLocation({ pathname: parsed.pathname })
    ) {
      return null;
    }

    return toStoredLocation({
      pathname: parsed.pathname,
      search: typeof parsed.search === 'string' ? parsed.search : '',
      hash: typeof parsed.hash === 'string' ? parsed.hash : '',
    });
  } catch {
    return null;
  }
};

export const isSameAuthOverlayLocation = (
  first: Pick<Location, 'pathname' | 'search' | 'hash'>,
  second: Pick<Location, 'pathname' | 'search' | 'hash'>,
): boolean => (
  first.pathname === second.pathname &&
  (first.search || '') === (second.search || '') &&
  (first.hash || '') === (second.hash || '')
);
